// Socket.IO client — реальный socket.io-client поверх AppSocketServer типизации.
//
// Транспорт:
//   - WebSocket с fallback на polling.
//   - Web: auth через httpOnly cookie + Bearer (если есть accessToken в localStorage).
//   - Capacitor: auth через Bearer-токен (cookie не работает в WebView).
//
// Reconnect:
//   - Exponential backoff: 1s → 5s (max), без потолка попыток.
//   - connectionStateRecovery на сервере (2 минуты): missed events будут
//     доставлены при reconnect в пределах окна (см. rules/realtime.md §1.1).
//   - После исчерпания старого лимита (10) socket сдавался навсегда; revival
//     на `online` / `visibilitychange` поднимает connect() снова.
//
// Lifecycle:
//   - connectSocket(opts) — идемпотентен, переиспользует существующий socket
//     если он подключён или подключается.
//   - disconnectSocket() — закрывает socket и сбрасывает singleton.
//
// API хука useSocket() / useSocketEvent(event, handler) — см.
// providers/socket-provider.tsx и hooks/use-socket-event.ts.

import type { ClientToServerEvents, ServerToClientEvents } from "@server/socket-events";
import { io, type Socket } from "socket.io-client";
import { logClientWarn } from "./client-log";
import { track } from "./observability";
import { IS_CAPACITOR } from "./platform-detect";

export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

interface ConnectOpts {
	/** Bearer token для Capacitor / опционально для web. */
	token?: string | null;
	/**
	 * URL Socket.IO сервера. По умолчанию — `/` (тот же origin, что и страница).
	 * Для Capacitor приложения нужно явно передать production-URL
	 * (`https://api.study.iec.exchange`).
	 */
	baseUrl?: string;
}

let socket: AppSocket | null = null;

/**
 * Эпизод деградации уже отмечен — второй раз не шлём.
 *
 * Reconnect у нас без потолка попыток, поэтому без этого флага заблокированный
 * транспорт (прокси, корпоративный фильтр, CSP страницы) залил бы аналитику
 * одинаковыми событиями раз в пять секунд до конца сессии. Сбрасывается на
 * успешном `connect` — следующий обрыв это уже новый эпизод.
 */
let degradationReported = false;

/**
 * Сообщить о деградации real-time канала — ровно один раз на эпизод.
 *
 * Сервер про неудачное подключение не знает по определению: до него запрос не
 * доходит. Значит единственный, кто может об этом рассказать, — клиент
 * (rules/logging.md §13a: любой откат на ухудшенный путь обязан кричать).
 */
function reportDegradation(kind: "polling-fallback" | "connect-failed", reason: string): void {
	if (degradationReported) return;
	degradationReported = true;
	track("realtime_degraded", { kind, reason });
	logClientWarn("[socket] real-time деградировал", undefined, { kind, reason });
}

/**
 * Текущий Socket.IO client (singleton). NULL если ещё не подключён или
 * был disconnect'нут.
 */
export function getSocket(): AppSocket | null {
	return socket;
}

/**
 * Подключает Socket.IO client. Идемпотентен — повторный вызов с тем же
 * token не пересоздаёт socket. При смене token (re-login) сначала вызови
 * `disconnectSocket()`, потом `connectSocket({ token: newToken })`.
 */
export function connectSocket(opts: ConnectOpts = {}): AppSocket {
	if (socket && (socket.connected || socket.active)) {
		return socket;
	}

	const baseUrl = opts.baseUrl ?? (import.meta.env?.VITE_SERVER_URL as string | undefined) ?? "/";

	const auth: Record<string, string> = {};
	if (opts.token) auth.token = opts.token;

	const next: AppSocket = io(baseUrl, {
		// Bearer в handshake.auth.token; сервер также принимает Authorization header.
		auth,
		// Web: cookie-auth (httpOnly refresh + better-auth session cookie).
		// Capacitor: cookies не работают в WebView → только Bearer.
		withCredentials: !IS_CAPACITOR,
		transports: ["websocket", "polling"],
		// Без этого порядок транспортов — не приоритет, а ультиматум: при отказе
		// websocket движок НЕ пробует следующий транспорт (`tryAllTransports` по
		// умолчанию false), а уходит в бесконечный reconnect тем же websocket'ом.
		// То есть любая среда, где режется именно апгрейд (корпоративный прокси,
		// CSP страницы, старый балансировщик), лишала приложение real-time
		// целиком, хотя рабочий polling был доступен рядом.
		tryAllTransports: true,
		reconnection: true,
		reconnectionAttempts: Number.POSITIVE_INFINITY,
		reconnectionDelay: 1000,
		reconnectionDelayMax: 5000,
		// path по умолчанию /socket.io/ — совпадает с серверным Fastify.
	});

	next.on("connect", () => {
		// Server-side connectionStateRecovery даёт missed events после reconnect
		// (2 мин окно). Логирование тонкое — реальный мониторинг в Hawk/PostHog.
		degradationReported = false;
		const transport = next.io.engine?.transport?.name;
		// Соединение есть, но не на websocket — это работающая, но ухудшенная
		// связь: выше задержки и нагрузка, и заметить её иначе нельзя.
		if (transport && transport !== "websocket") {
			reportDegradation("polling-fallback", transport);
		}
	});

	next.on("disconnect", (reason) => {
		logClientWarn("[socket] disconnected", undefined, { reason });
	});

	next.on("connect_error", (err) => {
		// При UNAUTHORIZED авто-reconnect бесполезен (сервер отбрасывает),
		// но socket.io-client всё равно ретраит — это ок, после re-login
		// new connectSocket() переподключится с актуальным токеном.
		// Отдельное событие для этого не нужно: сессия протухла — это не
		// деградация канала, а ожидаемый исход, его ловит session-expiry.
		if (err.message === "UNAUTHORIZED") return;
		reportDegradation("connect-failed", err.message);
	});

	socket = next;
	return next;
}

/**
 * Если singleton жив, но отключён (сеть, деплой, фон) — принудительно
 * `connect()`. Если singleton сброшен — поднимает новый.
 */
export function reviveSocket(opts: ConnectOpts = {}): AppSocket {
	if (socket && !socket.connected) {
		socket.connect();
		return socket;
	}
	return connectSocket(opts);
}

/**
 * Закрывает текущий socket и сбрасывает singleton. Вызывать при logout,
 * unmount root provider или при смене token (затем connectSocket).
 */
export function disconnectSocket(): void {
	if (socket) {
		socket.removeAllListeners();
		socket.disconnect();
		socket = null;
	}
}
